import { createApiRef, DiscoveryApi, IdentityApi } from '@backstage/core-plugin-api';
import {
  TriggerWorkflowInput,
  TriggerWorkflowResponse,
  RunStatusResponse,
  WorkflowListResponse,
} from './types';

/** API reference for the deployment plugin. */
export const deploymentApiRef = createApiRef<DeploymentApi>({
  id: 'plugin.deployment.service',
});

/** Contract for the deployment frontend API. */
export interface DeploymentApi {
  /** Trigger a GitHub Actions workflow_dispatch event. */
  triggerWorkflow(input: TriggerWorkflowInput): Promise<TriggerWorkflowResponse>;

  /** Get the status of a workflow run for a repository. */
  getRunStatus(
    owner: string,
    repo: string,
    options?: { runId?: number; workflowFile?: string },
  ): Promise<RunStatusResponse>;

  /** List all workflows in a repository with their latest run status. */
  listWorkflows(owner: string, repo: string): Promise<WorkflowListResponse>;
}

/** Default client that calls the deployment backend plugin via Backstage discovery. */
export class DeploymentClient implements DeploymentApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;

  constructor(options: { discoveryApi: DiscoveryApi; identityApi: IdentityApi }) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('deployment');
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const { token } = await this.identityApi.getCredentials();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  async triggerWorkflow(
    input: TriggerWorkflowInput,
  ): Promise<TriggerWorkflowResponse> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${baseUrl}/trigger`, {
      method: 'POST',
      headers,
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        body.error || `Failed to trigger workflow (${response.status})`,
      );
    }
    return response.json();
  }

  async getRunStatus(
    owner: string,
    repo: string,
    options?: { runId?: number; workflowFile?: string },
  ): Promise<RunStatusResponse> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (options?.runId) params.set('runId', String(options.runId));
    if (options?.workflowFile) params.set('workflow_file', options.workflowFile);
    const qs = params.toString();
    const url = `${baseUrl}/status/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}${qs ? `?${qs}` : ''}`;
    const headers = await this.getAuthHeaders();
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        body.error || `Failed to fetch run status (${response.status})`,
      );
    }
    return response.json();
  }

  async listWorkflows(
    owner: string,
    repo: string,
  ): Promise<WorkflowListResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/workflows/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}`;
    const headers = await this.getAuthHeaders();
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        body.error || `Failed to list workflows (${response.status})`,
      );
    }
    return response.json();
  }
}
